import * as Cesium from "cesium";

let drawnPointEntities = [];

/* Parse many formats and detect lat/lon order robustly */
export const drawCoordinatesStringOnMap = (viewer, coordinateString, connection, reset = false) => {
  if (!viewer || !coordinateString) return;

  // clear previous entities
  if (Array.isArray(drawnPointEntities) && drawnPointEntities.length) {
    drawnPointEntities.forEach((e) => viewer.entities.remove(e));
    drawnPointEntities = [];
  } else {
    drawnPointEntities = [];
  }
  if (reset) return;

  const raw = String(coordinateString).replace(/\r\n/g, "\n").trim();
  if (!raw) return;

  const positions = [];
  let idx = 0;

  // helpers
  const clamp = (v, a, b) => v >= a && v <= b;
  const isLat = (v) => Number.isFinite(v) && clamp(v, -90, 90);
  const isLon = (v) => Number.isFinite(v) && clamp(v, -180, 180);

  // parse DMS or decimal with hemisphere
  function parseToken(token) {
    if (!token) return null;
    const s = token
      .trim()
      .replace(/[^\w\.\-\s°'\"NSEWnsew\/]+/g, " ") // remove weird punctuation except degree chars
      .replace(/deg/gi, "°")
      .replace(/º/g, "°")
      .trim();

    // attempt DMS: deg ° min ' sec " optional hemisphere
    const dmsRegex = /([+-]?\d+(?:\.\d+)?)\s*(?:°|deg)?\s*([0-9]{1,2}(?:\.\d+)?)?\s*(?:['’′]|min)?\s*([0-9]{1,2}(?:\.\d+)?)?\s*(?:["”″]|sec)?\s*([NSEW])?/i;
    let m = s.match(dmsRegex);
    if (m && (m[2] !== undefined || m[3] !== undefined || /[NSEW]/i.test(s))) {
      const deg = Number(m[1]) || 0;
      const min = Number(m[2]) || 0;
      const sec = Number(m[3]) || 0;
      let val = Math.abs(deg) + min / 60 + sec / 3600;
      const hemi = (m[4] || "").toUpperCase();
      if (/[SW]/.test(hemi) || deg < 0) val = -val;
      return { value: deg < 0 && !/[NSEW]/i.test(hemi) ? -val : val, hemi: hemi || null };
    }

    // decimal with optional hemisphere (prefix or suffix)
    const decRegex = /([NSEW])?\s*([+-]?\d+(?:\.\d+)?)(?:\s*([NSEW]))?/i;
    m = s.match(decRegex);
    if (m) {
      const prefix = (m[1] || "").toUpperCase();
      const num = Number(m[2]);
      const suffix = (m[3] || "").toUpperCase();
      const hemi = prefix || suffix || null;
      let val = num;
      if (hemi === "S" || hemi === "W") val = Math.abs(num) * -1;
      if (hemi === "N" || hemi === "E") val = Math.abs(num);
      return { value: val, hemi };
    }

    // fallback: try plain number in string
    const numOnly = s.match(/-?\d+(?:\.\d+)?/);
    if (numOnly) {
      return { value: Number(numOnly[0]), hemi: null };
    }

    return null;
  }

  // split into candidate groups (WKT, lines, semicolons, pipes)
  const groups = [];
  // extract WKT POINT / MULTIPOINT /LINESTRING coordinates first
  const wktPointRe = /POINT\s*\(\s*([^\)]+)\s*\)/gi;
  let wktMatch;
  while ((wktMatch = wktPointRe.exec(raw)) !== null) {
    groups.push(wktMatch[1]);
  }
  // fallback split by lines and common separators
  if (groups.length === 0) {
    const rough = raw
      .split(/\n|;|\||\t/)
      .map((s) => s.trim())
      .filter(Boolean);
    rough.forEach((r) => {
      // sometimes multiple pairs appear in a single line separated by comma but as pair comma
      // preserve comma-separated pairs but treat brackets and words
      groups.push(r);
    });
  }

  // pair extraction per group
  const allNums = Array.from(raw.matchAll(/-?\d+(?:\.\d+)?/g)).map((m) => Number(m[0]));
  for (let i = 0; i < allNums.length; i += 2) {
    const a = allNums[i];
    const b = allNums[i + 1];
    if (!Number.isFinite(a) || !Number.isFinite(b)) continue;

    // detect order lat/lon
    let lat = a;
    let lon = b;
    if (!(a >= -90 && a <= 90 && b >= -180 && b <= 180)) {
      // maybe swapped
      if (b >= -90 && b <= 90 && a >= -180 && a <= 180) {
        lat = b;
        lon = a;
      }
    }

    const position = Cesium.Cartesian3.fromDegrees(lon, lat, 0);
    positions.push(position);

    const pointEntity = viewer.entities.add({
      name: `Point ${positions.length}`,
      position,
      point: {
        pixelSize: 10,
        color: Cesium.Color.BLUE,
        outlineColor: Cesium.Color.WHITE,
        outlineWidth: 2,
      },
      description: `Lat: ${lat}<br>Lon: ${lon}`,
    });
    drawnPointEntities.push(pointEntity);
  }

  // optional connection line
  if (connection && positions.length > 1) {
    const lineEntity = viewer.entities.add({
      name: "Connected Line",
      polyline: {
        positions,
        width: 4,
        material: Cesium.Color.RED,
        clampToGround: false,
      },
    });
    drawnPointEntities.push(lineEntity);
  }

  if (drawnPointEntities.length) {
    viewer.zoomTo(drawnPointEntities);
  }
};
